import { useSyncExternalStore } from 'react';

/**
 * UI sound effects.
 *
 * Plays short, soft feedback sounds for interactions (taps, toasts, modals) and
 * celebratory game moments (cheers, scores, missions, badges). Sounds are ON by
 * default and remembered across sessions in localStorage; users can mute them
 * from Settings. Mirrors the structure of useDataSaver.js.
 *
 * Implementation notes:
 * - Howler and the audio files are imported/fetched lazily on the first play(),
 *   so they add nothing to the initial bundle or page load.
 * - play() reads the stored flag itself, so components can call it directly
 *   without subscribing — only the Settings toggle needs the reactive hook.
 * - Browsers block audio until the first user gesture; Howler auto-unlocks on
 *   that first interaction, so at most the very first sound is skipped.
 */
const KEY = 'soundEnabled';
const EVENT = 'soundenabledchange';
const VOLUME = 0.4; // soft by default

// Files live in /public/sounds/<name>.mp3. Keep in sync with scripts/gen-sounds.cjs.
const SOUNDS = [
  'tap',
  'toast-success',
  'toast-error',
  'modal-open',
  'modal-close',
  'cheer',
  'score-submit',
  'mission-complete',
  'badge-earned',
  'leaderboard',
];

function prefersReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function isSoundOn() {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored !== null) return stored === '1';
    // Default on, unless the user has asked the OS to reduce motion/effects.
    return !prefersReducedMotion();
  } catch {
    // Private mode / storage disabled — fall back to the default.
    return !prefersReducedMotion();
  }
}

/** Manual toggle. Persists across sessions and notifies subscribers. */
export function setSoundEnabled(on) {
  try {
    localStorage.setItem(KEY, on ? '1' : '0');
  } catch {
    /* ignore storage failures — the event below still updates the UI */
  }
  window.dispatchEvent(new Event(EVENT));
}

// --- playback -------------------------------------------------------------

let howlerModule; // Promise<typeof import('howler')>
const howls = {}; // name -> Howl

function getHowl(name) {
  if (howls[name]) return Promise.resolve(howls[name]);
  if (!howlerModule) howlerModule = import('howler');
  return howlerModule.then(({ Howl }) => {
    if (!howls[name]) {
      howls[name] = new Howl({
        src: [`/sounds/${name}.mp3`],
        volume: VOLUME,
        preload: true,
      });
    }
    return howls[name];
  });
}

/**
 * Play a named sound effect. No-ops silently when sound is muted or the name is
 * unknown, and never throws (playback rejections are swallowed).
 */
export function play(name) {
  if (!isSoundOn()) return;
  if (!SOUNDS.includes(name)) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[useSound] unknown sound "${name}"`);
    }
    return;
  }
  getHowl(name).then((howl) => howl.play()).catch(() => {});
}

// --- reactive hook --------------------------------------------------------

function subscribe(callback) {
  window.addEventListener(EVENT, callback);
  window.addEventListener('storage', callback); // other tabs toggling it
  return () => {
    window.removeEventListener(EVENT, callback);
    window.removeEventListener('storage', callback);
  };
}

/**
 * Reactive access to the sound preference. Returns { enabled, toggle, play }.
 * Components that only emit sounds can import { play } directly instead.
 */
export default function useSound() {
  const enabled = useSyncExternalStore(subscribe, isSoundOn, () => true);
  return {
    enabled,
    toggle: () => setSoundEnabled(!enabled),
    setEnabled: setSoundEnabled,
    play,
  };
}
